/*
 * Copyright (C) Regione Lombardia
 */
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { headerProjectCache } from "@core/utilities";
import { environment } from "@env/environment";
import {
  AnagraficaClasseDocumento,
  AnagraficaClassificazioneOpera,
  AnagraficaComuni,
  AnagraficaEnte,
  AnagraficaEvento,
  AnagraficaFascePercentuali,
  AnagraficaFonte,
  AnagraficaFunzionePrevalente,
  AnagraficaImportanzaOpera,
  AnagraficaLegge,
  AnagraficaLivelloDanno,
  AnagraficaMacroTipologiaOpera,
  AnagraficaMateriale,
  AnagraficaProvenienza,
  AnagraficaProvince,
  AnagraficaRuolo,
  AnagraficaSponda,
  AnagraficaStatoOpera,
  AnagraficaStatoProgetto,
  AnagraficaTipologiaIntervento,
  AnagraficaTipologiaOpera,
  AnagraficaTipologiaRilevamento,
} from "@models";
import { Observable } from "rxjs";
import { AnagraficaComunitaMontana } from "src/app/_models/anagrafica/anagrafica-comunita-montana";
import { AnagraficaConsorzi } from "src/app/_models/anagrafica/anagrafica-consorzi";
import { AnagraficaTipoShape } from "src/app/_models/anagrafica/anagrafica-tipo-shape";
import { ResponseAnagraficaLegge } from "src/app/_models/anagrafica/response/response-anagrafica-legge";

@Injectable({
  providedIn: "root",
})
export class AnagraficaService {
  constructor(private httpClient: HttpClient) {}

  public getClasseDocumento(): Observable<AnagraficaClasseDocumento[]> {
    return this.httpClient.get<AnagraficaClasseDocumento[]>(
      `${environment.urlApi}/private/anagrafica/classe-documento`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getComuni(): Observable<AnagraficaComuni[]> {
    return this.httpClient.get<AnagraficaComuni[]>(
      `${environment.urlApi}/private/anagrafica/comuni`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getEnte(): Observable<AnagraficaEnte[]> {
    return this.httpClient.get<AnagraficaEnte[]>(
      `${environment.urlApi}/private/anagrafica/ente`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getEvento(): Observable<AnagraficaEvento[]> {
    return this.httpClient.get<AnagraficaEvento[]>(
      `${environment.urlApi}/private/anagrafica/evento`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getFascePercentuali(): Observable<AnagraficaFascePercentuali[]> {
    return this.httpClient.get<AnagraficaFascePercentuali[]>(
      `${environment.urlApi}/private/anagrafica/fasce-percentuali`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getFonte(): Observable<AnagraficaFonte[]> {
    return this.httpClient.get<AnagraficaFonte[]>(
      `${environment.urlApi}/private/anagrafica/fonte`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getFunzionePrevalente(): Observable<AnagraficaFunzionePrevalente[]> {
    return this.httpClient.get<AnagraficaFunzionePrevalente[]>(
      `${environment.urlApi}/private/anagrafica/funzione-prevalente`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getImportanzaOpera(): Observable<AnagraficaImportanzaOpera[]> {
    return this.httpClient.get<AnagraficaImportanzaOpera[]>(
      `${environment.urlApi}/private/anagrafica/importanza-opera`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getLegge(): Observable<AnagraficaLegge[]> {
    return this.httpClient.get<AnagraficaLegge[]>(
      `${environment.urlApi}/private/anagrafica/legge`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public search(
    values: AnagraficaLegge,
    paginatorQueryParams: string
  ): Observable<ResponseAnagraficaLegge> {
    return this.httpClient.post<ResponseAnagraficaLegge>(
      `${environment.urlApi}/private/anagrafica/legge-filtrata?${paginatorQueryParams}`,
      values
    );
  }

  public insertLegge(
    anagraficaLegge: AnagraficaLegge
  ): Observable<AnagraficaLegge> {
    return this.httpClient.post<AnagraficaLegge>(
      `${environment.urlApi}/private/anagrafica/inserimento-legge`,
      anagraficaLegge
    );
  }

  public updateLegge(
    anagraficaLegge: AnagraficaLegge
  ): Observable<AnagraficaLegge> {
    return this.httpClient.put<AnagraficaLegge>(
      `${environment.urlApi}/private/anagrafica/inserimento-legge`,
      anagraficaLegge
    );
  }

  public findLegge(id: Number): Observable<AnagraficaLegge> {
    return this.httpClient.post<AnagraficaLegge>(
      `${environment.urlApi}/private/anagrafica/find-legge`,
      id
    );
  }

  public getLivelloDanno(): Observable<AnagraficaLivelloDanno[]> {
    return this.httpClient.get<AnagraficaLivelloDanno[]>(
      `${environment.urlApi}/private/anagrafica/livello-danno`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getMacroTipologiaOpera(): Observable<AnagraficaMacroTipologiaOpera[]> {
    return this.httpClient.get<AnagraficaMacroTipologiaOpera[]>(
      `${environment.urlApi}/private/anagrafica/macro-tipologia-opera`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getMateriale(): Observable<AnagraficaMateriale[]> {
    return this.httpClient.get<AnagraficaMateriale[]>(
      `${environment.urlApi}/private/anagrafica/materiale`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getProvenienza(): Observable<AnagraficaProvenienza[]> {
    return this.httpClient.get<AnagraficaProvenienza[]>(
      `${environment.urlApi}/private/anagrafica/provenienza`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getProvince(): Observable<AnagraficaProvince[]> {
    return this.httpClient.get<AnagraficaProvince[]>(
      `${environment.urlApi}/private/anagrafica/province`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getRuolo(): Observable<AnagraficaRuolo[]> {
    return this.httpClient.get<AnagraficaRuolo[]>(
      `${environment.urlApi}/private/anagrafica/ruolo`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getSponda(): Observable<AnagraficaSponda[]> {
    return this.httpClient.get<AnagraficaSponda[]>(
      `${environment.urlApi}/private/anagrafica/sponda`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getStatoOpera(): Observable<AnagraficaStatoOpera[]> {
    return this.httpClient.get<AnagraficaStatoOpera[]>(
      `${environment.urlApi}/private/anagrafica/stato-opera`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getStatoProgetto(): Observable<AnagraficaStatoProgetto[]> {
    return this.httpClient.get<AnagraficaStatoProgetto[]>(
      `${environment.urlApi}/private/anagrafica/stato-progetto`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getTipologiaIntervento(): Observable<AnagraficaTipologiaIntervento[]> {
    return this.httpClient.get<AnagraficaTipologiaIntervento[]>(
      `${environment.urlApi}/private/anagrafica/tipologia-intervento`,
      {
        headers: headerProjectCache(),
      }
    );
  }

   public getTipologiaInterventoNew(): Observable<AnagraficaTipologiaIntervento[]> {
    return this.httpClient.get<AnagraficaTipologiaIntervento[]>(
      `${environment.urlApi}/private/anagrafica/tipologia-intervento-new`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getTipologiaOpera(): Observable<AnagraficaTipologiaOpera[]> {
    return this.httpClient.get<AnagraficaTipologiaOpera[]>(
      `${environment.urlApi}/private/anagrafica/tipologia-opera`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getClassificazioneOpera(): Observable<AnagraficaClassificazioneOpera[]> {
    return this.httpClient.get<AnagraficaClassificazioneOpera[]>(
      `${environment.urlApi}/private/anagrafica/classificazione-opera`,
      {
        headers: headerProjectCache(),
      }
    );
  }


  public getTipologiaRilevamento(): Observable<
    AnagraficaTipologiaRilevamento[]
  > {
    return this.httpClient.get<AnagraficaTipologiaRilevamento[]>(
      `${environment.urlApi}/private/anagrafica/tipologia-rilevamento`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getTipoShape(): Observable<AnagraficaTipoShape[]> {
    return this.httpClient.get<AnagraficaTipoShape[]>(
      `${environment.urlApi}/private/anagrafica/tipo-shape`,
      {
        headers: headerProjectCache(),
      }
    );
  }

  public getDescrizioneLivelloDanno(idDanno: string): Observable<AnagraficaLivelloDanno> {
    return this.httpClient.get<AnagraficaLivelloDanno>(
        `${environment.urlApi}/private/anagrafica/livello-danno/${idDanno}`,
        {
          headers: headerProjectCache(),
        }
    );
  }

  public getListaComunitaMontane(): Observable<AnagraficaComunitaMontana[]> {
    return this.httpClient.get<AnagraficaComunitaMontana[]>(
        `${environment.urlApi}/private/anagrafica/comunita-montane`,
        {
          headers: headerProjectCache(),
        }
    );
  }

  public getListaConsorzi(): Observable<AnagraficaConsorzi[]> {
    return this.httpClient.get<AnagraficaConsorzi[]>(
        `${environment.urlApi}/private/anagrafica/consorzio`,
        {
          headers: headerProjectCache(),
        }
    );
  }
}
